import {
  MOVEMENT_THRESHOLD,
  TERMINAL_THRESHOLD,
  TERMINAL_RADIUS,
  MAX_SNAP_DISTANCE,
  DIRECTION_CHECK_THRESHOLD,
} from "./mapConfig";

// Calculate distance between two points (in meters)
export const calculateDistance = (p1, p2) => {
  if (!p1 || !p2) return Infinity;
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (p1.lat * Math.PI) / 180;
  const φ2 = (p2.lat * Math.PI) / 180;
  const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
  const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Find nearest point on polyline
export const findNearestPointOnRoute = (position, directions) => {
  if (!position || !directions?.routes?.[0]) return null;
  const route = directions.routes[0];
  let closestPoint = null;
  let minDistance = Infinity;
  let routeIndex = -1;
  let currentRouteIndex = 0;

  route.legs.forEach((leg) => {
    leg.steps.forEach((step) => {
      const path = step.path;
      path.forEach((point, index) => {
        if (index === 0) return;
        const getLat = (p) => (typeof p.lat === "function" ? p.lat() : p.lat);
        const getLng = (p) => (typeof p.lng === "function" ? p.lng() : p.lng);

        const start = {
          lat: getLat(path[index - 1]),
          lng: getLng(path[index - 1]),
        };
        const end = {
          lat: getLat(point),
          lng: getLng(point),
        };

        const dx = end.lng - start.lng;
        const dy = end.lat - start.lat;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);
        if (segmentLength === 0) return;

        const t =
          ((position.lng - start.lng) * dx + (position.lat - start.lat) * dy) /
          (segmentLength * segmentLength);

        if (t < 0) {
          const distToStart = calculateDistance(position, start);
          if (distToStart < minDistance) {
            minDistance = distToStart;
            closestPoint = start;
            routeIndex = currentRouteIndex;
          }
          return;
        }

        if (t > 1) {
          const distToEnd = calculateDistance(position, end);
          if (distToEnd < minDistance) {
            minDistance = distToEnd;
            closestPoint = end;
            routeIndex = currentRouteIndex + 1;
          }
          return;
        }

        const projectedPoint = {
          lat: start.lat + t * dy,
          lng: start.lng + t * dx,
        };
        const distance = calculateDistance(position, projectedPoint);
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = projectedPoint;
          routeIndex = currentRouteIndex + t;
        }

        currentRouteIndex++;
      });
    });
  });

  if (minDistance <= MAX_SNAP_DISTANCE) {
    return {
      ...closestPoint,
      routeIndex: routeIndex,
    };
  }
  return null;
};

// Check if the vehicle is moving
export const detectVehicleMovement = (newPosition, lastPosition) => {
  if (!lastPosition || !newPosition) return true;
  const distance = calculateDistance(newPosition, lastPosition);
  return distance > MOVEMENT_THRESHOLD;
};

// Extract route path points from directions
export const extractPathPoints = (directions) => {
  if (!directions?.routes?.[0]?.legs) return [];
  let points = [];
  directions.routes[0].legs.forEach((leg) => {
    leg.steps.forEach((step) => {
      const path = step.path;
      path.forEach((point) => {
        points.push({
          lat: typeof point.lat === "function" ? point.lat() : point.lat,
          lng: typeof point.lng === "function" ? point.lng() : point.lng,
        });
      });
    });
  });
  return points;
};

// Split the route at the current position
export const splitRouteAtPosition = (allPoints, position, pathDirection, calculateDistance) => {
  if (!allPoints.length || !position) {
    return { before: [], after: [] };
  }
  if (!pathDirection) {
    return { before: allPoints, after: [] };
  }
  let splitIndex = 0;
  let minDistance = Infinity;
  allPoints.forEach((point, index) => {
    const distance = calculateDistance(position, point);
    if (distance < minDistance) {
      minDistance = distance;
      splitIndex = index;
    }
  });
  if (pathDirection === "source_to_dest") {
    return {
      before: allPoints.slice(0, splitIndex + 1),
      after: allPoints.slice(splitIndex),
    };
  } else {
    return {
      before: allPoints.slice(splitIndex),
      after: allPoints.slice(0, splitIndex + 1),
    };
  }
};

// Determine vehicle direction based on position changes
export const checkDirectionChange = (positions, stops) => {
  if (
    positions.length < DIRECTION_CHECK_THRESHOLD ||
    !stops ||
    stops.length < 2
  ) {
    return null;
  }
  const source = {
    lat: parseFloat(stops[0].latitude),
    lng: parseFloat(stops[0].longitude),
  };
  const destination = {
    lat: parseFloat(stops[stops.length - 1].latitude),
    lng: parseFloat(stops[stops.length - 1].longitude),
  };
  const firstPos = positions[0];
  const lastPos = positions[positions.length - 1];
  const firstPosToSource = calculateDistance(firstPos, source);
  const firstPosToDest = calculateDistance(firstPos, destination);
  const lastPosToSource = calculateDistance(lastPos, source);
  const lastPosToDest = calculateDistance(lastPos, destination);
  const sourceDistanceChange = lastPosToSource - firstPosToSource;
  const destDistanceChange = lastPosToDest - firstPosToDest;
  if (
    Math.abs(sourceDistanceChange) > 10 ||
    Math.abs(destDistanceChange) > 10
  ) {
    if (sourceDistanceChange > 0 && destDistanceChange < 0) {
      return "source_to_dest";
    } else if (sourceDistanceChange < 0 && destDistanceChange > 0) {
      return "dest_to_source";
    }
  }
  return null;
};

// Check if the vehicle has completed its journey
export const isJourneyComplete = (
  currentPosition,
  stops,
  pathDirection,
  routePaths,
  fullPath,
  isMoving,
  stoppedDuration
) => {
  if (!stops || stops.length === 0 || !currentPosition || !pathDirection) {
    return false;
  }
  const finalStop =
    pathDirection === "source_to_dest" ? stops[stops.length - 1] : stops[0];
  const finalStopPosition = {
    lat: parseFloat(finalStop.latitude),
    lng: parseFloat(finalStop.longitude),
  };
  const distanceToFinal = calculateDistance(currentPosition, finalStopPosition);

  const routeCompletionRatio =
    routePaths.before.length /
    (routePaths.before.length + routePaths.after.length);

  let isNearTerminalSection = false;
  if (fullPath.length > 0) {
    const terminalSectionStartIdx = Math.floor(fullPath.length * 0.9);
    const terminalSection = fullPath.slice(terminalSectionStartIdx);
    let minDistanceToTerminalSection = Infinity;
    terminalSection.forEach((point) => {
      const distance = calculateDistance(currentPosition, point);
      minDistanceToTerminalSection = Math.min(
        minDistanceToTerminalSection,
        distance
      );
    });
    isNearTerminalSection = minDistanceToTerminalSection < TERMINAL_RADIUS;
  }
  const isCloseToFinalStop = distanceToFinal < TERMINAL_RADIUS;
  const hasCompletedMostOfRoute = routeCompletionRatio > 0.9;
  const isStoppedNearEnd =
    !isMoving &&
    (isCloseToFinalStop || isNearTerminalSection) &&
    stoppedDuration > 30;

  const isComplete =
    (isCloseToFinalStop && hasCompletedMostOfRoute) ||
    (isNearTerminalSection && hasCompletedMostOfRoute) ||
    isStoppedNearEnd;

  return isComplete;
};

// Helper to determine initial direction
export const determineInitialDirection = (currentPosition, stops) => {
  if (!stops || stops.length < 2 || !currentPosition) return null;
  const source = {
    lat: parseFloat(stops[0].latitude),
    lng: parseFloat(stops[0].longitude),
  };
  const destination = {
    lat: parseFloat(stops[stops.length - 1].latitude),
    lng: parseFloat(stops[stops.length - 1].longitude),
  };
  const distToSource = calculateDistance(currentPosition, source);
  const distToDest = calculateDistance(currentPosition, destination);
  if (distToSource < TERMINAL_THRESHOLD && distToSource < distToDest) {
    return "source_to_dest";
  }
  if (distToDest < TERMINAL_THRESHOLD && distToDest < distToSource) {
    return "dest_to_source";
  }
  return null;
};